/** Métriques santé stock (partagé Dashboard + vue Stock).
 *
 * FIX 2026-05 : on exclut les véhicules non-cotables (neufs catalogue,
 * `skip_cotation`, `positionnement === 'inconnu'`) avant tout calcul.
 * Sinon le dashboard compte « 1 annonce surcotée » alors que la vue Stock
 * (qui filtre déjà ces véhicules) ne l'affiche pas — l'utilisateur ne
 * peut pas la retrouver depuis le compteur, et le score est faussé par
 * des annonces qui ne sont structurellement pas comparables au marché
 * d'occasion. */
function isCotable(a) {
  if (a?.skip_cotation === true) return false
  const p = String(a?.positionnement || '')
  if (p === 'neuf' || p === 'inconnu') return false
  return true
}

export function computeStockHealthMetrics(annonces, seuilStockLong) {
  const rawRows = Array.isArray(annonces) ? annonces : []
  const rows = rawRows.filter(isCotable)
  const analyses = rows
  const surcotes = rows.filter((a) => Number.isFinite(Number(a?.ecart_pct)) && Number(a.ecart_pct) >= 10)
  const souscotes = rows.filter((a) => Number.isFinite(Number(a?.ecart_pct)) && Number(a.ecart_pct) <= -10)
  const dansMarche = rows.filter((a) => Number.isFinite(Number(a?.ecart_pct)) && Math.abs(Number(a.ecart_pct)) < 10)
  const stockLong = rows.filter((a) => Number(a?.jours_en_ligne) > seuilStockLong)
  const liquiditesMoy =
    analyses.length > 0 ? analyses.reduce((s, a) => s + (Number(a?.score_liquidite) || 5), 0) / analyses.length : 5

  // Score sur 100, base 60, modulé par 4 facteurs **proportionnels au parc**.
  //
  // FIX 2026-05 : avant on retirait `3 points × stockLong.length` en absolu,
  // ce qui plafonnait n'importe quel concessionnaire moto à 0/100 dès qu'il
  // avait ~20 motos en stock long (alors que la rotation moto est lente par
  // nature, c'est normal d'avoir une part importante de stock > seuil). Un
  // pro avec 69 véhicules dont 63 « dans le marché » et 2 surcotées doit
  // arriver à un score « à surveiller » (50-70), pas à 0.
  //
  // Désormais, chaque pénalité est calculée comme une **part du parc total**
  // (rows.length), avec un poids comparable aux autres signaux, ce qui rend
  // le score lisible quelle que soit la taille du stock.
  const parc = Math.max(rows.length, 1)
  const partDansMarche = dansMarche.length / parc
  const partSurcotes = surcotes.length / parc
  const partStockLong = stockLong.length / parc
  let score = 60
  score += partDansMarche * 20    // +20 si tout est dans le marché
  score -= partSurcotes * 25      // -25 si tout est surcoté
  score += (liquiditesMoy - 5) * 3 // ±15 selon liquidité moyenne (1..10)
  score -= partStockLong * 25     // -25 si tout est en stock long
  score = Math.min(100, Math.max(0, Math.round(score)))

  const scoreColor = score >= 75 ? '#4ade80' : score >= 50 ? '#fbbf24' : '#f87171'
  const scoreLabel = score >= 75 ? 'Stock en bonne santé' : score >= 50 ? 'Stock à surveiller' : 'Stock à optimiser'

  const riskRows = rows
    .map((a) => {
      const jours = Number(a?.jours_en_ligne)
      const marche = Number(a?.valeur_marche)
      if (!Number.isFinite(jours) || jours <= seuilStockLong || !Number.isFinite(marche) || marche <= 0) return null
      const depreciationParJour = Math.max(1, Math.round((marche * 0.018) / 30))
      const perteEstimee30j = depreciationParJour * 30
      return {
        ...a,
        depreciation_par_jour: depreciationParJour,
        perte_estimee_30j: perteEstimee30j,
      }
    })
    .filter(Boolean)
    .sort((a, b) => (Number(b?.perte_estimee_30j) || 0) - (Number(a?.perte_estimee_30j) || 0))

  const riskTotal = riskRows.reduce((s, r) => s + (Number(r?.perte_estimee_30j) || 0), 0)
  const agingBuckets = [
    {
      key: '0_30',
      label: '0-30j',
      color: '#4ade80',
      count: rows.filter((a) => Number(a?.jours_en_ligne) >= 0 && Number(a?.jours_en_ligne) <= 30).length,
    },
    {
      key: '31_60',
      label: '31-60j',
      color: '#60a5fa',
      count: rows.filter((a) => Number(a?.jours_en_ligne) > 30 && Number(a?.jours_en_ligne) <= 60).length,
    },
    {
      key: '61_90',
      label: '61-90j',
      color: '#fbbf24',
      count: rows.filter((a) => Number(a?.jours_en_ligne) > 60 && Number(a?.jours_en_ligne) <= 90).length,
    },
    {
      key: '90_plus',
      label: `>${seuilStockLong}j`,
      color: '#f87171',
      count: rows.filter((a) => Number(a?.jours_en_ligne) > seuilStockLong).length,
    },
  ]

  return {
    analyses,
    surcotes,
    souscotes,
    dansMarche,
    stockLong,
    liquiditesMoy,
    score,
    scoreColor,
    scoreLabel,
    riskRows,
    riskTotal,
    agingBuckets,
  }
}
